const localizationMotion = new WeakMap();
const fallbackLocalizationShells = new WeakMap();
const activeLocalizationSheets = new Set();
const localizationExitDuration = 580;
const localizationSearch = new WeakMap();
const localizationNames = new Map();

// A hydrated header is rendered at ?section_id=... . Never navigate back to
// that fragment after Shopify updates the customer's country or language.
function calvielleLocalizationReturnTo(href, origin) {
  try {
    const url = new URL(href, origin);
    if (url.origin !== origin || url.username || url.password || url.pathname.startsWith('//')) return '/';
    for (const key of ['section_id', 'sections', 'sections_url', 'country', 'currency', 'language', 'hmac', 'host', 'shop', 'timestamp']) {
      url.searchParams.delete(key);
    }
    return url.pathname + url.search + url.hash;
  } catch (_) {
    return '/';
  }
}

function prepareLocalizationReturn(form) {
  if (!form?.matches('.cv-localization__form')) return;
  let input = form.querySelector('input[name="return_to"]');
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'return_to';
    form.appendChild(input);
  }
  input.value = calvielleLocalizationReturnTo(window.location.href, window.location.origin);
}
const commonCountryAliases = {
  AU: '澳大利亚 澳大利亞 澳洲 澳国', US: '美国 美國 美利坚 美利堅 USA America',
  GB: '英国 英國 英伦 英倫 UK Britain', CA: '加拿大 加国 加國',
  CN: '中国 中國 中国大陆 中國大陸 大陆 大陸', HK: '香港 Hongkong',
  MO: '澳门 澳門 Macao Macau', TW: '台湾 台灣 臺灣',
  SG: '新加坡 狮城 獅城', NZ: '新西兰 新西蘭 纽西兰 紐西蘭',
  JP: '日本', KR: '韩国 韓國 南韩 南韓', AE: '阿联酋 阿聯酋 UAE',
  FR: '法国 法國', DE: '德国 德國', IT: '意大利 義大利',
  ES: '西班牙', CH: '瑞士', MY: '马来西亚 馬來西亞 大马 大馬',
  TH: '泰国 泰國', VN: '越南', IN: '印度', ID: '印度尼西亚 印度尼西亞 印尼',
  PH: '菲律宾 菲律賓', SA: '沙特阿拉伯 沙特 沙烏地阿拉伯',
  BR: '巴西', MX: '墨西哥', ZA: '南非'
};
const commonCurrencyAliases = {
  AUD: '澳元 澳币 澳幣 澳刀 A$', USD: '美元 美元 美金 美刀 US$',
  CNY: '人民币 人民幣 RMB 元', EUR: '欧元 歐元 欧币 歐幣 €',
  GBP: '英镑 英鎊 镑 鎊 £', CAD: '加元 加币 加幣 C$',
  HKD: '港元 港币 港幣 HK$', TWD: '台币 台幣 臺幣 新台币 新臺幣 NT$',
  JPY: '日元 日圆 日圓 日币 日幣 円 ¥', KRW: '韩元 韓元 韩币 韓幣 ₩',
  SGD: '新加坡元 新币 新幣 新元 S$', NZD: '纽币 紐幣 纽元 紐元 新西兰元 NZ$',
  CHF: '瑞士法郎 瑞郎', INR: '印度卢比 印度盧比 卢比 盧比 ₹',
  THB: '泰铢 泰銖 ฿', VND: '越南盾 ₫', AED: '阿联酋迪拉姆 阿聯酋迪拉姆 迪拉姆'
};

function normalizeLocalizationQuery(value) {
  let text = String(value || '');
  if (typeof text.normalize === 'function') text = text.normalize('NFKD');
  return text.replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

function localizedSearchName(code, type, locale) {
  if (!code || typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') return '';
  const key = `${locale}:${type}`;
  try {
    if (!localizationNames.has(key)) localizationNames.set(key, new Intl.DisplayNames([locale], { type }));
    return localizationNames.get(key).of(code) || '';
  } catch (_) {
    // A missing locale or an unsupported code never makes the selector unusable.
    return '';
  }
}

function localizationSearchOption(button) {
  const country = (button.dataset.countryCode || button.value || '').toUpperCase();
  const currency = (button.dataset.currencyCode || '').toUpperCase();
  const countryNames = [button.dataset.countryName, ...(commonCountryAliases[country] || '').split(/\s+/)];
  const currencyNames = [button.dataset.currencySymbol, ...(commonCurrencyAliases[currency] || '').split(/\s+/)];
  const aliases = [button.dataset.countryName, button.textContent, country, currency,
    button.dataset.currencySymbol, commonCountryAliases[country], commonCurrencyAliases[currency]];
  for (const locale of ['en', 'zh-Hans', 'zh-Hant']) {
    const regionName = localizedSearchName(country, 'region', locale);
    countryNames.push(regionName);
    aliases.push(regionName);
    const currencyName = localizedSearchName(currency, 'currency', locale);
    currencyNames.push(currencyName);
    aliases.push(currencyName);
  }
  return {
    button, country, currency,
    countryNames: new Set(countryNames.filter(Boolean).map(normalizeLocalizationQuery)),
    currencyNames: new Set(currencyNames.filter(Boolean).map(normalizeLocalizationQuery)),
    searchable: normalizeLocalizationQuery(aliases.filter(Boolean).join(' '))
  };
}

function filterLocalizationCountries(search) {
  const query = normalizeLocalizationQuery(search.input.value);
  const upper = query.toUpperCase();
  const countryCode = ({ UK: 'GB', USA: 'US' })[upper] || upper;
  const exactCountry = /^[A-Z]{2}$/.test(countryCode) && search.options.some(option => option.country === countryCode);
  // A complete country name should not also match every place using its currency
  // (for example Australia versus Australian Dollar / 澳大利亚 versus 澳大利亚元).
  const exactCountryName = !!query && search.options.some(option => option.countryNames.has(query));
  const exactCurrency = /^[a-z]{3}$/.test(query) && search.options.some(option => option.currency === upper);
  const exactCurrencyName = !!query && search.options.some(option => option.currencyNames.has(query));
  const tokens = query.split(' ').filter(Boolean);
  let visible = 0;
  for (const option of search.options) {
    const matches = exactCountry ? option.country === countryCode
      : exactCountryName ? option.countryNames.has(query)
      : exactCurrency ? option.currency === upper
      : exactCurrencyName ? option.currencyNames.has(query)
      : tokens.every(token => option.searchable.includes(token));
    option.button.hidden = !matches;
    option.button.inert = !matches;
    if (matches) visible += 1;
  }
  search.clear.hidden = !search.input.value;
  search.status.hidden = !query || visible > 0;
  search.list.scrollTop = 0;
  return visible;
}

function disposeLocalizationSearch(shell) {
  const search = shell && localizationSearch.get(shell);
  if (!search) return;
  search.input.removeEventListener('input', search.onInput);
  search.input.removeEventListener('search', search.onInput);
  search.input.removeEventListener('keydown', search.onKeydown);
  search.clear.removeEventListener('click', search.onClear);
  localizationSearch.delete(shell);
}

function prepareLocalizationSearch(shell, reset) {
  const input = shell.querySelector('[data-cv-localization-search]');
  const clear = shell.querySelector('[data-cv-localization-search-clear]');
  const status = shell.querySelector('[data-cv-localization-search-status]');
  const list = shell.querySelector('[data-cv-localization-country-options]');
  if (!input || !clear || !status || !list) return;
  let search = localizationSearch.get(shell);
  if (!search || search.input !== input || search.clear !== clear) {
    disposeLocalizationSearch(shell);
    search = { input, clear, status, list, options: [] };
    search.onInput = () => filterLocalizationCountries(search);
    search.onKeydown = event => {
      // This search is outside the Shopify form; guard Enter without selecting
      // the first matching country or interfering with IME composition.
      if (event.key === 'Enter' && !event.isComposing) event.preventDefault();
    };
    search.onClear = () => {
      input.value = '';
      filterLocalizationCountries(search);
      input.focus({ preventScroll: true });
    };
    input.addEventListener('input', search.onInput);
    input.addEventListener('search', search.onInput);
    input.addEventListener('keydown', search.onKeydown);
    clear.addEventListener('click', search.onClear);
    localizationSearch.set(shell, search);
  }
  search.status = status;
  search.list = list;
  search.options = [...list.querySelectorAll('[data-cv-localization-country]')].map(localizationSearchOption);
  if (reset) input.value = '';
  filterLocalizationCountries(search);
}

function cancelLocalizationMotion(state) {
  state.epoch += 1;
  window.cancelAnimationFrame(state.frame);
  window.clearTimeout(state.timer);
  state.frame = null;
  state.timer = null;
}

function isTopLocalizationSheet(shell) {
  return [...activeLocalizationSheets].pop() === shell;
}

function releaseLocalizationSheet(state) {
  window.removeEventListener('keydown', state.onKeydown, true);
  window.removeEventListener('focusin', state.onFocusin, true);
  activeLocalizationSheets.delete(state.shell);
  if (!activeLocalizationSheets.size) {
    document.documentElement.classList.remove('cv-localization-open');
    document.documentElement.style.removeProperty('--cv-localization-scrollbar-gap');
  }
}

function getLocalizationMotion(shell) {
  let state = localizationMotion.get(shell);
  if (state) return state;
  state = { shell, phase: 'closed', epoch: 0, frame: null, timer: null, lastFocused: null };
  state.onKeydown = (event) => {
    if (!isTopLocalizationSheet(shell)) return;
    if (event.key === 'Escape' && !event.isComposing) {
      event.preventDefault();
      event.stopPropagation();
      closeLocalizationSheet(shell);
      return;
    }
    if (event.key !== 'Tab') return;
    // The sheet is portaled outside its parent menu. Keep the menu's own
    // document-level focus trap paused until this sheet has finished closing.
    event.stopPropagation();
    const focusable = [...state.panel.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href]')]
      .filter((element) => element instanceof HTMLElement && element.offsetParent !== null);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  state.onFocusin = (event) => {
    if (!isTopLocalizationSheet(shell)) return;
    // Window capture runs before the nested menu's document capture listener,
    // including a trap installed after the menu's entrance animation ends.
    event.stopPropagation();
    if (!shell.contains(event.target)) {
      state.panel.querySelector('.cv-localization__close')?.focus({ preventScroll: true });
    }
  };
  localizationMotion.set(shell, state);
  return state;
}

function openLocalizationSheet(shell, trigger) {
  const panel = shell?.querySelector('[data-cv-localization-panel]');
  if (!panel || !trigger) return;
  shell.querySelectorAll('.cv-localization__form').forEach(prepareLocalizationReturn);
  const state = getLocalizationMotion(shell);
  if (state.phase === 'opening' || state.phase === 'open') return;
  const wasHidden = shell.hidden;
  prepareLocalizationSearch(shell, wasHidden);
  cancelLocalizationMotion(state);
  state.panel = panel;
  state.trigger = trigger;
  state.phase = 'opening';
  if (wasHidden) state.lastFocused = document.activeElement;
  if (!activeLocalizationSheets.size) {
    const gap = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    document.documentElement.style.setProperty('--cv-localization-scrollbar-gap', `${gap}px`);
  }
  activeLocalizationSheets.add(shell);
  document.documentElement.classList.add('cv-localization-open');
  shell.hidden = false;
  trigger.setAttribute('aria-expanded', 'true');
  window.addEventListener('keydown', state.onKeydown, true);
  window.addEventListener('focusin', state.onFocusin, true);
  const epoch = state.epoch;
  const reveal = () => {
    if (state.epoch !== epoch || state.phase !== 'opening' || !shell.isConnected) return;
    state.frame = null;
    shell.classList.add('is-open');
    state.phase = 'open';
    panel.querySelector('.cv-localization__close')?.focus({ preventScroll: true });
  };
  if (!wasHidden || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    // A closing sheet stays rendered, so CSS can reverse from its current frame.
    reveal();
  } else {
    state.frame = window.requestAnimationFrame(() => {
      if (state.epoch !== epoch || state.phase !== 'opening') return;
      state.frame = window.requestAnimationFrame(reveal);
    });
  }
}

function closeLocalizationSheet(shell) {
  const state = shell && localizationMotion.get(shell);
  if (!state || shell.hidden || state.phase === 'closing' || state.phase === 'closed') return;
  const wasRevealed = shell.classList.contains('is-open');
  cancelLocalizationMotion(state);
  state.phase = 'closing';
  shell.classList.remove('is-open', 'is-submitting');
  state.trigger?.setAttribute('aria-expanded', 'false');
  const epoch = state.epoch;
  const finish = () => {
    if (state.epoch !== epoch || state.phase !== 'closing') return;
    state.timer = null;
    shell.hidden = true;
    state.phase = 'closed';
    releaseLocalizationSheet(state);
    if (state.lastFocused instanceof HTMLElement && state.lastFocused.isConnected) {
      state.lastFocused.focus({ preventScroll: true });
    }
  };
  if (!wasRevealed || window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
  else state.timer = window.setTimeout(finish, localizationExitDuration);
}

function disposeLocalizationSheet(shell) {
  disposeLocalizationSearch(shell);
  const state = shell && localizationMotion.get(shell);
  if (!state) return;
  cancelLocalizationMotion(state);
  state.phase = 'closed';
  state.trigger?.setAttribute('aria-expanded', 'false');
  shell.classList.remove('is-open', 'is-submitting');
  shell.hidden = true;
  releaseLocalizationSheet(state);
  localizationMotion.delete(shell);
}

class CalvielleLocalizationSheet extends HTMLElement {
  constructor() {
    super();
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onShellClick = this.onShellClick.bind(this);
    this.onShellSubmit = this.onShellSubmit.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onTriggerClick);
    this.refreshShell();
  }

  disconnectedCallback() {
    disposeLocalizationSheet(this.shell);
    this.removeEventListener('click', this.onTriggerClick);
    this.boundShell?.removeEventListener('click', this.onShellClick);
    this.boundShell?.removeEventListener('submit', this.onShellSubmit);
    this.boundShell = null;
    this.shell?.remove();
  }

  refreshShell() {
    this.trigger = this.querySelector('[data-cv-localization-trigger]');
    const controlledPanelId = this.trigger?.getAttribute('aria-controls');
    const localShell = this.querySelector('[data-cv-localization-shell]');
    const controlledShell = controlledPanelId
      ? document.getElementById(controlledPanelId)?.closest('[data-cv-localization-shell]')
      : null;
    const nextShell = localShell || controlledShell || this.shell;

    if (localShell && this.shell && this.shell !== localShell) {
      disposeLocalizationSheet(this.shell);
      this.shell.remove();
    }

    this.shell = nextShell;
    this.panel = this.shell?.querySelector('[data-cv-localization-panel]');
    if (!this.trigger || !this.shell || !this.panel) return false;

    if (this.boundShell !== this.shell) {
      this.boundShell?.removeEventListener('click', this.onShellClick);
      this.boundShell?.removeEventListener('submit', this.onShellSubmit);
      this.boundShell = this.shell;
      this.boundShell.addEventListener('click', this.onShellClick);
      this.boundShell.addEventListener('submit', this.onShellSubmit);
    }

    // The menu drawer is dynamically redrawn. Move the newest shell back to
    // the document root whenever the trigger is used so fixed positioning and
    // scroll locking remain reliable after every redraw.
    if (this.shell.parentElement !== document.body) {
      document.body.appendChild(this.shell);
    }

    return true;
  }

  onTriggerClick(event) {
    const trigger = event.target.closest('[data-cv-localization-trigger]');
    if (!trigger || !this.contains(trigger)) return;

    this.trigger = trigger;
    if (this.refreshShell()) this.open();
  }

  onShellClick(event) {
    if (event.target.closest('[data-cv-localization-close]')) this.close();
  }

  onShellSubmit(event) {
    if (event.target.matches('.cv-localization__form')) {
      prepareLocalizationReturn(event.target);
      this.shell?.classList.add('is-submitting');
    }
  }

  open() {
    if (!this.refreshShell()) return;
    openLocalizationSheet(this.shell, this.trigger);
  }

  close() {
    closeLocalizationSheet(this.shell);
  }
}

if (!customElements.get('calvielle-localization-sheet')) {
  customElements.define('calvielle-localization-sheet', CalvielleLocalizationSheet);
}

function closeFallbackSheet(shell) {
  closeLocalizationSheet(shell);
}

function openFallbackSheet(trigger, sheet) {
  const controlledPanelId = trigger.getAttribute('aria-controls');
  const localShell = sheet.querySelector('[data-cv-localization-shell]');
  const controlledShell = controlledPanelId
    ? document.getElementById(controlledPanelId)?.closest('[data-cv-localization-shell]')
    : null;
  const shell = localShell || controlledShell;
  const panel = shell?.querySelector('[data-cv-localization-panel]');
  if (!shell || !panel) return;

  // During a redraw the old portaled shell and the replacement can briefly
  // share an ID. getElementById alone can resolve the new one and miss the old.
  const previousShell = fallbackLocalizationShells.get(sheet);
  for (const outdated of new Set([previousShell, controlledShell])) {
    if (outdated && outdated !== shell) {
      disposeLocalizationSheet(outdated);
      outdated.remove();
    }
  }
  fallbackLocalizationShells.set(sheet, shell);
  if (shell.parentElement !== document.body) document.body.appendChild(shell);
  shell.dataset.cvLocalizationFallback = 'true';
  openLocalizationSheet(shell, trigger);
}

// Horizon redraws the drawer contents when it opens. A capture-phase bridge
// guarantees that the newest trigger is reconnected before the drawer's own
// click handlers can replace it again.
if (!window.calvielleLocalizationDelegated) {
  window.calvielleLocalizationDelegated = true;
  document.addEventListener(
    'click',
    (event) => {
      const closeControl = event.target.closest('[data-cv-localization-close]');
      const fallbackShell = closeControl?.closest('[data-cv-localization-shell][data-cv-localization-fallback="true"]');
      if (fallbackShell) {
        closeFallbackSheet(fallbackShell);
        return;
      }

      const trigger = event.target.closest('[data-cv-localization-trigger]');
      const sheet = trigger?.closest('calvielle-localization-sheet');
      if (!trigger || !sheet) return;

      if (typeof sheet.open === 'function') {
        sheet.trigger = trigger;
        sheet.refreshShell();
        sheet.open();
      } else {
        openFallbackSheet(trigger, sheet);
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest('.cv-localization__form');
      prepareLocalizationReturn(form);
      const shell = form?.closest('[data-cv-localization-shell]');
      if (shell) shell.classList.add('is-submitting');
    },
    true
  );
}
